import { ComponentProps } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  type Path,
  type PathValue,
  UseFormSetValue,
} from "react-hook-form";
import { Platform } from "react-native";
import { z } from "zod";

import { Grid, getGridLayout } from "@/components";
import { Input } from "@/components/forms/Input";
import Select from "@/components/forms/Select";
import { US_STATES } from "@/settings/constants";
import { AddressAutocomplete } from "./AddressAutocomplete";

type AutoComplete = ComponentProps<typeof Input.Controlled>["autoComplete"];

export const addressSchema = z.object({
  streetAddress: z.string().min(1, "Please enter your home address"),
  streetAddress2: z.string().optional(),
  city: z.string().min(1, "Please enter your city"),
  state: z.string().min(1, "Please select a state"),
  zipCode: z
    .string()
    .min(1, "Please enter your zip code")
    .regex(/^\d{5}$/, "Zip code must be 5 digits"),
});

export type AddressFormData = z.infer<typeof addressSchema>;

interface AddressFieldsProps<T extends AddressFormData> {
  control: Control<T>;
  errors: FieldErrors<AddressFormData>;
  setValue: UseFormSetValue<T>;
  zipcodeError?: string;
}

/**
 * The home address inputs, for any form whose values include `AddressFormData`. Picking an
 * autocomplete suggestion fills the rest of the address.
 */
export function AddressFields<T extends AddressFormData>({
  control,
  errors,
  setValue,
  zipcodeError,
}: AddressFieldsProps<T>) {
  // The field names are fixed by `AddressFormData`; react-hook-form only needs them as paths of `T`.
  const fieldPath = (name: keyof AddressFormData) => name as Path<T>;

  return (
    <Grid>
      <Controller
        control={control}
        name={fieldPath("streetAddress")}
        render={({ field }) => (
          <AddressAutocomplete
            ref={field.ref}
            name={field.name}
            label="Home address"
            placeholder="Home address"
            value={field.value ?? ""}
            onChangeText={field.onChange}
            errorText={errors.streetAddress?.message}
            onSelectAddress={(address) => {
              const set = (
                name: "streetAddress" | "city" | "state" | "zipCode",
                fieldValue: string,
              ) =>
                setValue(fieldPath(name), fieldValue as PathValue<T, Path<T>>, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              // Always replace every field so picking a new address can't
              // leave a stale city/state/zip from a prior selection — that
              // mismatch is exactly what gets rejected downstream by Persona.
              set("streetAddress", address.streetAddress);
              set("city", address.city ?? "");
              set("state", address.state ?? "");
              set("zipCode", address.zipCode ?? "");
            }}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name={fieldPath("streetAddress2")}
        render={({ field }) => (
          <Input.Controlled
            label="Apartment, suite, etc."
            autoComplete="address-line2"
            textContentType="streetAddressLine2"
            {...field}
            value={field.value ?? ""}
            placeholder="Apartment, suite, etc."
            errorText={errors.streetAddress2?.message}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name={fieldPath("city")}
        render={({ field }) => (
          <Input.Controlled
            label="City"
            autoComplete={Platform.select({
              web: "address-level2" as AutoComplete,
              default: "postal-address-locality",
            })}
            textContentType="addressCity"
            {...field}
            value={field.value ?? ""}
            placeholder="City"
            errorText={errors.city?.message}
            rootProps={getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name={fieldPath("state")}
        render={({ field }) => (
          <Select
            ref={field.ref}
            name={field.name}
            label="State"
            items={US_STATES}
            value={field.value ?? ""}
            onValueChange={field.onChange}
            placeholder="Select state"
            errorText={errors.state?.message}
            {...getGridLayout(6)}
          />
        )}
      />

      <Controller
        control={control}
        name={fieldPath("zipCode")}
        render={({ field }) => (
          <Input.Controlled
            label="Zip code"
            autoComplete="postal-code"
            textContentType="postalCode"
            {...field}
            value={field.value ?? ""}
            inputMode="numeric"
            maxLength={5}
            placeholder="Zip code"
            errorText={zipcodeError || errors.zipCode?.message}
            rootProps={getGridLayout(6)}
          />
        )}
      />
    </Grid>
  );
}
